import React from "react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/layout/HomePageLayout";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

const Privacy = () => {
  return (
    <PageLayout>
      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-0">
        <h1 className="text-2xl font-bold mb-4">Privacy Policy</h1>

        <div className="prose max-w-none [&_p]:text-base [&_p]:leading-7 [&_p]:my-0 [&_p]:mb-4 [&_h2]:leading-tight [&_h2]:mb-4 [&_h2]:mt-8 [&_h3]:leading-tight [&_h3]:mb-3 [&_h3]:mt-6 [&_ul]:my-0 [&_ul]:mb-4 [&_ol]:my-0 [&_ol]:mb-4 [&_li]:text-base [&_li]:leading-7 [&_li]:m-0 [&_li]:pl-1 [&_li>p]:m-0">
          <p className="text-base text-muted-foreground mb-8">
            Last updated: May 3, 2025
          </p>

          <Separator className="my-8" />

          <div className="space-y-6">
            <p>
              This Privacy Policy describes how World Class Design ("we", "us",
              or "our") collects, uses, stores, and shares your personal
              information when you use our services. It also explains your
              rights under the General Data Protection Regulation (GDPR) and the
              California Consumer Privacy Act (CCPA).
            </p>

            <h2 className="text-2xl font-semibold mt-8">
              1. Information We Collect
            </h2>
            <p>
              For purposes of this Privacy Policy, "personal data" refers to any
              information relating to an identified or identifiable natural
              person (GDPR Article 4).
            </p>

            <h3 className="text-xl font-semibold mt-6">1.1 Personal Data</h3>
            <p>
              We may collect the following categories of personal data. Required
              fields are marked as mandatory, while others are optional and help
              us enhance your experience.
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Identity Data (mandatory): Name, username, date of birth.</li>
              <li>Contact Data (mandatory): Email address.</li>
              <li>Contact Data (optional): Phone number, mailing address.</li>
              <li>
                Account Data (optional): Login credentials, profile information,
                preferences.
              </li>
              <li>
                Payment Data (optional): Credit/debit card details, billing
                address (processed securely by our payment provider).
              </li>
            </ul>

            <h3 className="text-xl font-semibold mt-6">1.2 Usage Data</h3>
            <p>
              We automatically collect information about how you access and use
              our services, including:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Device & Connection Data: IP address, browser type, operating
                system, device identifiers.
              </li>
              <li>
                Usage Logs: Pages visited, time spent on pages, features used,
                clickstream data.
              </li>
              <li>
                Analytics Data: Cookies, web beacons, and similar technologies
                to understand usage patterns.
              </li>
            </ul>

            <h3 className="text-xl font-semibold mt-6">1.3 Third-Party Data</h3>
            <p>
              We may receive data from third parties, such as social media
              platforms (if you link your account), analytics providers, or
              marketing partners.
            </p>

            <h2 className="text-2xl font-semibold mt-8">
              2. How We Use Your Information
            </h2>
            <p>We use personal data for the following purposes:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                To Provide Services: Process transactions, maintain your
                account, deliver features, and respond to your requests.
              </li>
              <li>
                To Improve Services: Analyze usage patterns, conduct research,
                and troubleshoot technical problems.
              </li>
              <li>
                To Communicate: Send updates, newsletters, marketing
                communications (with your consent), and respond to customer
                service inquiries.
              </li>
              <li>
                To Comply with Legal Obligations: Prevent fraud, enforce our
                Terms of Service, and comply with court orders or legal
                requirements.
              </li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8">
              3. Data Retention and Storage
            </h2>
            <h3 className="text-xl font-semibold mt-6">3.1 Retention Period</h3>
            <p>
              We retain personal data for the following periods, unless
              otherwise required by law:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Identity Data: Up to 2 years after account closure or
                inactivity.
              </li>
              <li>
                Contact Data: Up to 2 years after the last interaction or
                account deletion.
              </li>
              <li>Account Data: Up to 90 days after account termination.</li>
              <li>
                Payment Data: Up to 7 years (to comply with financial
                regulations).
              </li>
              <li>Usage Logs & Analytics Data: Up to 12 months.</li>
              <li>
                Third‑Party Data: Retained according to the original source
                provider's policy, up to 2 years.
              </li>
            </ul>

            <h3 className="text-xl font-semibold mt-6">3.2 Storage Location</h3>
            <p>
              Your data is stored on secure servers located in Africa (with data
              centers in South Africa and Kenya). We implement industry-standard
              security measures (encryption, access controls) to protect your
              information.
            </p>

            <h2 className="text-2xl font-semibold mt-8">
              4. Data Sharing and Disclosure
            </h2>
            <h3 className="text-xl font-semibold mt-6">
              4.1 Service Providers
            </h3>
            <p>
              We may share your data with third-party service providers who
              perform services on our behalf, such as payment processing,
              hosting, analytics, and marketing.
            </p>

            <h3 className="text-xl font-semibold mt-6">
              4.2 Legal Requirements
            </h3>
            <p>
              We may disclose your information if required by law, court order,
              or governmental authority, or to protect our rights, privacy,
              safety, or property.
            </p>

            <h3 className="text-xl font-semibold mt-6">
              4.3 Business Transfers
            </h3>
            <p>
              If we merge with, are acquired by, or sell all or part of our
              assets, your personal data may be transferred to the new owner.
            </p>

            <h2 className="text-2xl font-semibold mt-8">
              5. International Data Transfers
            </h2>
            <p>
              Your personal data may be transferred and stored outside your
              country of residence. We ensure adequate safeguards, such as EU
              Standard Contractual Clauses, are in place to protect your
              information.
            </p>

            <h2 className="text-2xl font-semibold mt-8">6. Your Rights</h2>
            <h3 className="text-xl font-semibold mt-6">6.1 GDPR Rights</h3>
            <p>If you are an EU resident, you have the right to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Access: Request a copy of your personal data.</li>
              <li>
                Rectification: Correct inaccurate or incomplete information.
              </li>
              <li>
                Erasure: Delete your personal data ("right to be forgotten"),
                subject to legal exceptions.
              </li>
              <li>
                Restrict Processing: Pause processing under certain conditions.
              </li>
              <li>
                Portability: Receive your data in a structured, machine-readable
                format.
              </li>
              <li>
                Object: Object to processing based on legitimate interests.
              </li>
              <li>
                Withdraw Consent: Withdraw consent at any time for processing
                based on consent.
              </li>
              <li>
                Lodge a Complaint: Lodge a complaint with a supervisory
                authority in your EU member state.
              </li>
            </ul>

            <h3 className="text-xl font-semibold mt-6">6.2 CCPA Rights</h3>
            <p>If you are a California resident, you have the right to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Know: Request disclosure of categories and specific pieces of
                personal data collected.
              </li>
              <li>
                Delete: Request deletion of personal data, with certain
                exceptions.
              </li>
              <li>Opt-Out: Instruct us not to sell your personal data.</li>
              <li>
                Non-Discrimination: Not be discriminated against for exercising
                your rights.
              </li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8">
              7. Cookies and Tracking
            </h2>
            <p>
              We use cookies and similar technologies to enhance your
              experience. You can manage cookie preferences through your browser
              settings or our cookie banner.
            </p>

            <h2 className="text-2xl font-semibold mt-8">
              8. Security Measures
            </h2>
            <p>
              We implement reasonable technical and organizational measures—such
              as encryption, access controls, regular vulnerability scans, and
              an incident response plan—to protect against unauthorized access,
              disclosure, alteration, or destruction of personal data.
            </p>

            <h2 className="text-2xl font-semibold mt-8">
              9. Children's Privacy
            </h2>
            <p>
              Our services are not intended for individuals under 16 (or the
              minimum age in your jurisdiction). Where we lawfully target
              minors, we obtain verifiable parental or guardian consent prior to
              collecting personal data.
            </p>

            <h2 className="text-2xl font-semibold mt-8">10. Contact Us</h2>
            <p>
              If you have questions or concerns about this Privacy Policy or our
              data practices, please contact us at hi@wcd.school.
            </p>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Privacy;
